import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { usePathHistoryContext } from '@context/PathHistoryContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageSection } from '@ui/components/PageSection'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getDomainName } from '@utils/cookie.utils'
import { useNotificationUnread } from '@utils/notifications'

import { scrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { NotificationPreview } from './NotificationPreview'

import styles from './NotificationCenter.module.scss'

export const NotificationDetailsPage: React.FC = () => {
  const { notificationId = '', clientNotificationId = '' } = useParams<{
    notificationId: string
    clientNotificationId: string
  }>()
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()
  const { refreshAllNotifications } = useNotificationUnread()
  const { navigateToPreviousPath } = usePathHistoryContext()
  const navigate = useNavigate()

  const removeNotification = async () => {
    await apiClient.deleteNotification(notificationId, clientNotificationId)
    await queryClient.invalidateQueries({ queryKey: ['notification'] })
    await refreshAllNotifications()
    navigate('/profile/notifications')
  }

  const readNotification = async (notificationId: string) => {
    await apiClient.readNotification(notificationId, clientNotificationId)
    await queryClient.invalidateQueries({ queryKey: ['notification'] })
    await refreshAllNotifications()
  }

  const { data: notification, isLoading } = useQuery({
    queryKey: queryKeys.notification.byId(notificationId, locale, clientNotificationId),
    queryFn: () =>
      apiClient
        .getNotification(
          notificationId,
          {
            languageId: locale,
            domain: getDomainName(true),
          },
          clientNotificationId
        )
        .then((response) => {
          scrollToTop()
          return response
        }),
    enabled: !!notificationId,
  })

  useEffect(() => {
    if (!notification && !isLoading) {
      navigate('/profile/notifications')
      return
    }
    if (notification && !notification.isRead) {
      readNotification(notification.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, isLoading])

  return (
    <div className={styles.notifications}>
      <PageSection onGoBack={navigateToPreviousPath} title={t('Notifications.Notifications')} />

      <Loading isLoading={isLoading}>
        {notification && (
          <NotificationPreview
            remove={removeNotification}
            notification={{ ...notification, isRead: true }}
          />
        )}
      </Loading>
    </div>
  )
}
